import { useState, useEffect, useRef, useCallback } from 'react'
import { getFileTypeInfo, truncateFilename } from '../utils/fileHelpers'
import { saveMediaWithPicker, isIOS } from '../utils/mediaHelpers'

/* ───────────────────────────────────────────────────────────────
 *  PDF VIEWER CONFIG
 * ─────────────────────────────────────────────────────────────── */
const PDF_CONFIG = {
  renderScale: 1.5,
  firstPageScale: 1.5,
  jpegQuality: 0.85,
  lazyLoadMargin: 1.5,
  maxConcurrentRenders: 2,
}

/* ───────────────────────────────────────────────────────────────
 *  UNPREVIEWABLE CANVAS CONFIG — Apple Music vivid style
 * ─────────────────────────────────────────────────────────────── */
const FALLBACK_CONFIG = {
  enabled: true,

  blob1: { x: 18,  y: 28, size: 90, opacity: 0.85, hueShift: 0    },
  blob2: { x: 82,  y: 78, size: 95, opacity: 0.75, hueShift: -18  },
  blob3: { x: 55,  y: 50, size: 70, opacity: 0.45, hueShift: 12   },

  baseWashOpacity: 0.08,
  minBrightness: 90,
  darkTextThreshold: 175,
  grainOpacity: 0.04,

  animate: true,
  animationDurationSec: 22,

  filenameMaxLength: 40,
  filenameFontSize: '36px',
  filenameFontWeight: 700,
  filenameLetterSpacing: '-0.6px',
  filenameMarginBottom: '10px',

  labelFontSize: '13px',
  labelFontWeight: 600,
  labelOpacity: 0.6,
  labelLetterSpacing: '1.5px',

  pillFontSize: '15px',
  pillFontWeight: 600,
  pillPaddingY: '15px',
  pillPaddingX: '34px',
  pillRadius: '100px',
  pillBottomOffset: '56px',
  pillGlassBackground: 'rgba(255, 255, 255, 0.78)',
  pillGlassBorder: '0.5px solid rgba(255, 255, 255, 0.6)',
  pillGlassBlur: '24px',
  pillGlassSaturate: '180%',
  pillShadow: '0 10px 30px rgba(0, 0, 0, 0.45), 0 0 0 0.5px rgba(0, 0, 0, 0.1)',
}

const SFChevronLeft = ({ size = 22 }) => <svg width={size} height={size} viewBox="0 0 7 11" fill="currentColor"><path d="M0 5.18555C0.00585938 4.98633 0.0761719 4.81641 0.234375 4.66406L4.80469 0.193359C4.92773 0.0644531 5.0918 0 5.2793 0C5.66016 0 5.95898 0.292969 5.95898 0.673828C5.95898 0.861328 5.88281 1.03125 5.75391 1.16602L1.63477 5.18555L5.75391 9.20508C5.88281 9.33398 5.95898 9.50391 5.95898 9.69141C5.95898 10.0781 5.66016 10.3711 5.2793 10.3711C5.0918 10.3711 4.92773 10.3066 4.80469 10.1777L0.234375 5.70703C0.0761719 5.55469 0 5.38477 0 5.18555Z"/></svg>
const SFShareUp = ({ size = 24 }) => <svg width={size} height={size} viewBox="0 0 11 14" fill="currentColor"><path d="M5.19727 8.57227C4.94531 8.57227 4.73438 8.36719 4.73438 8.12109V2.15625L4.77539 1.27148L4.44141 1.62891L3.5332 2.5957C3.45117 2.68945 3.32227 2.73633 3.21094 2.73633C2.96484 2.73633 2.78906 2.56055 2.78906 2.32617C2.78906 2.19727 2.8418 2.10352 2.92969 2.01562L4.85742 0.152344C4.98047 0.0351562 5.08008 0 5.19727 0C5.32031 0 5.41992 0.0351562 5.53711 0.152344L7.4707 2.01562C7.55859 2.10352 7.60547 2.19727 7.60547 2.32617C7.60547 2.56055 7.42383 2.73633 7.17773 2.73633C7.06641 2.73633 6.94336 2.68945 6.86133 2.5957L5.95898 1.62891L5.625 1.27734L5.66602 2.15625V8.12109C5.66602 8.36719 5.44922 8.57227 5.19727 8.57227ZM2.44922 13.1602C0.873047 13.1602 0 12.2812 0 10.7109V6.19922C0 4.62305 0.873047 3.75 2.44922 3.75H3.63281V4.69336H2.44922C1.48828 4.69336 0.943359 5.23242 0.943359 6.19922V10.7109C0.943359 11.6777 1.48828 12.2168 2.44922 12.2168H7.94531C8.91211 12.2168 9.45703 11.6777 9.45703 10.7109V6.19922C9.45703 5.23242 8.91211 4.69336 7.94531 4.69336H6.76172V3.75H7.94531C9.52148 3.75 10.4004 4.62305 10.4004 6.19922V10.7109C10.4004 12.2812 9.52148 13.1602 7.94531 13.1602H2.44922Z"/></svg>
const SFSaveDown = ({ size = 24 }) => <svg width={size} height={size} viewBox="0 0 11 13" fill="currentColor"><path d="M3.21094 5.83594C3.32812 5.83594 3.45117 5.88281 3.5332 5.97656L4.44141 6.94336L4.77539 7.29492L4.73438 6.41016V0.451172C4.73438 0.205078 4.95117 0 5.20312 0C5.45508 0 5.66602 0.205078 5.66602 0.451172V6.41016L5.625 7.29492L5.95898 6.94336L6.86719 5.97656C6.94922 5.88281 7.06641 5.83594 7.18359 5.83594C7.42383 5.83594 7.61133 6.00586 7.61133 6.24609C7.61133 6.375 7.55859 6.46875 7.4707 6.55664L5.53711 8.41992C5.41992 8.53711 5.32031 8.57227 5.20312 8.57227C5.08008 8.57227 4.98047 8.53711 4.86328 8.41992L2.92969 6.55664C2.8418 6.46875 2.78906 6.375 2.78906 6.24609C2.78906 6.00586 2.96484 5.83594 3.21094 5.83594ZM2.44922 12.5273C0.873047 12.5273 0 11.6484 0 10.0781V5.56641C0 3.99023 0.873047 3.11719 2.44922 3.11719H3.63281V4.06055H2.44922C1.48828 4.06055 0.943359 4.60547 0.943359 5.56641V10.0781C0.943359 11.0391 1.48828 11.584 2.44922 11.584H7.94531C8.91211 11.584 9.45703 11.0391 9.45703 10.0781V5.56641C9.45703 4.60547 8.91211 4.06055 7.94531 4.06055H6.76172V3.11719H7.94531C9.52148 3.11719 10.4004 3.99023 10.4004 5.56641V10.0781C10.4004 11.6484 9.52148 12.5273 7.94531 12.5273H2.44922Z"/></svg>
const SFShareArrow = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 13 11" fill="currentColor"><path d="M6.86133 10.8984C6.41016 10.8984 6.08203 10.5645 6.08203 10.1191V8.00391H5.93555C3.7793 8.00391 2.46094 8.625 1.5293 10.3594C1.30664 10.7812 1.01953 10.875 0.714844 10.875C0.310547 10.875 0 10.5 0 9.7793C0 5.52539 1.86914 2.91211 5.93555 2.91211H6.08203V0.814453C6.08203 0.363281 6.41016 0 6.87305 0C7.18359 0 7.40039 0.128906 7.74023 0.439453L12.3809 4.76953C12.6094 4.98633 12.6914 5.22656 12.6914 5.44922C12.6914 5.66016 12.6094 5.91211 12.3809 6.12891L7.74023 10.4883C7.42969 10.7695 7.17773 10.8984 6.86133 10.8984ZM7.23047 9.41016C7.26562 9.41016 7.30078 9.39258 7.33008 9.36328L11.3496 5.56055C11.3965 5.51367 11.4141 5.48438 11.4141 5.44922C11.4141 5.41406 11.3965 5.38477 11.3496 5.33789L7.33008 1.5C7.30664 1.4707 7.27148 1.45312 7.23633 1.45312C7.18359 1.45312 7.14844 1.48828 7.14844 1.54102V3.76172C7.14844 3.89648 7.08984 3.95508 6.95508 3.95508H6.19922C2.39648 3.95508 1.08398 6.48633 0.990234 9.26953C0.990234 9.31055 1.00195 9.32812 1.03125 9.32812C1.05469 9.32812 1.06641 9.31055 1.08398 9.27539C1.78711 7.81641 3.50391 6.94922 6.19922 6.94922H6.95508C7.08984 7.00781 7.14844 7.00781 7.14844 7.14258V9.31641C7.14844 9.36914 7.18359 9.41016 7.23047 9.41016Z"/></svg>
const SFTrashFill = ({ size = 24, color = 'currentColor' }) => <svg width={size} height={size} viewBox="0 0 12 14" fill={color}><path d="M3.08789 13.2246C2.17969 13.2246 1.60547 12.6855 1.56445 11.7832L1.16602 3.33398H0.515625C0.234375 3.33398 0 3.11133 0 2.81836C0 2.53125 0.234375 2.30859 0.515625 2.30859H3.11719V1.42969C3.11719 0.533203 3.69141 0 4.6582 0H6.88477C7.85742 0 8.42578 0.533203 8.42578 1.42969V2.30859H11.0332C11.3262 2.30859 11.5547 2.53125 11.5547 2.81836C11.5547 3.11133 11.3262 3.33398 11.0332 3.33398H10.3887L9.99023 11.7773C9.94922 12.6797 9.36914 13.2246 8.47266 13.2246H3.08789ZM4.19531 1.48242V2.30859H7.34766V1.48242C7.34766 1.17773 7.13672 0.984375 6.80859 0.984375H4.73438C4.40625 0.984375 4.19531 1.17773 4.19531 1.48242ZM3.97852 11.4082C4.23633 11.4082 4.40625 11.2441 4.39453 11.0039L4.21289 4.63477C4.20703 4.38281 4.03125 4.23047 3.79102 4.23047C3.5332 4.23047 3.36914 4.38867 3.375 4.63477L3.55078 11.0098C3.5625 11.2559 3.72656 11.4082 3.97852 11.4082ZM5.77734 11.4082C6.0293 11.4082 6.20508 11.25 6.20508 11.0098V4.63477C6.20508 4.38867 6.0293 4.23047 5.77734 4.23047C5.52539 4.23047 5.34961 4.38867 5.34961 4.63477V11.0098C5.34961 11.25 5.52539 11.4082 5.77734 11.4082ZM7.57617 11.4082C7.82227 11.4082 7.98633 11.2559 7.99805 11.0098L8.17969 4.63477C8.18555 4.38867 8.02148 4.23047 7.76367 4.23047C7.51758 4.23047 7.34766 4.38867 7.3418 4.63477L7.1543 11.0039C7.14844 11.25 7.31836 11.4082 7.57617 11.4082Z"/></svg>

/* ───────────────────────────────────────────────────────────────
 *  Header buttons — TWO variants
 *
 *  Default (dark mode) = white glass on dark bg, used over image /
 *  PDF / Office where buttons sit on varied content and need to be
 *  readable. Bar behind them adds extra contrast.
 *
 *  Light variant = darker glass tint + dark icons, used when floating
 *  over a bright UnpreviewableCanvas (yellow JS, orange Illustrator).
 *  Same blur, same shape, just inverted tones so the buttons look
 *  like frosted glass on the color instead of dark blobs covering it.
 * ─────────────────────────────────────────────────────────────── */
const glassCircle = {
  width: '44px', height: '44px', borderRadius: '50%',
  background: 'rgba(255,255,255,0.08)',
  backdropFilter: 'saturate(180%) blur(20px)',
  WebkitBackdropFilter: 'saturate(180%) blur(20px)',
  border: '0.5px solid rgba(255,255,255,0.12)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#fff', padding: 0,
}
const glassPill = {
  width: '65px', height: '44px', borderRadius: '100px',
  background: 'rgba(255,255,255,0.08)',
  backdropFilter: 'saturate(180%) blur(20px)',
  WebkitBackdropFilter: 'saturate(180%) blur(20px)',
  border: '0.5px solid rgba(255,255,255,0.12)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#fff', padding: 0, textDecoration: 'none',
}

// Light-bg variants — used when floating over the colored canvas.
// Tint flips: background is now dark-translucent (a "smoked" feel over
// the bright color), icon color flips to near-black for contrast.
const glassCircleLight = {
  ...glassCircle,
  background: 'rgba(0, 0, 0, 0.14)',
  border: '0.5px solid rgba(0, 0, 0, 0.08)',
  color: 'rgba(20, 20, 22, 0.9)',
}
const glassPillLight = {
  ...glassPill,
  background: 'rgba(0, 0, 0, 0.14)',
  border: '0.5px solid rgba(0, 0, 0, 0.08)',
  color: 'rgba(20, 20, 22, 0.9)',
}

/* ───────────────────────────────────────────────────────────────
 *  COLOR HELPERS
 * ─────────────────────────────────────────────────────────────── */
function hexToRgb(hex) {
  if (!hex) return { r: 100, g: 100, b: 100 }
  const m = hex.replace('#', '')
  const full = m.length === 3 ? m.split('').map(c => c + c).join('') : m.padEnd(6, '0')
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  }
}

function luminance({ r, g, b }) {
  return Math.round(0.299 * r + 0.587 * g + 0.114 * b)
}

function liftColor(rgb, minBrightness) {
  const lum = luminance(rgb)
  if (lum >= minBrightness) return rgb
  const ratio = minBrightness / Math.max(lum, 1)
  return {
    r: Math.min(255, Math.round(rgb.r * ratio + 20)),
    g: Math.min(255, Math.round(rgb.g * ratio + 20)),
    b: Math.min(255, Math.round(rgb.b * ratio + 20)),
  }
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      case b: h = (r - g) / d + 4; break
      default: break
    }
    h *= 60
  }
  return { h, s: s * 100, l: l * 100 }
}

function hslStr({ h, s, l }, alpha = 1) {
  return `hsla(${h}, ${s}%, ${l}%, ${alpha})`
}

function shiftHue(hsl, deltaDeg) {
  return { h: (hsl.h + deltaDeg + 360) % 360, s: hsl.s, l: hsl.l }
}

/* Helper used by DocumentViewer to decide whether to float the header.
 * Mirrors the logic inside UnpreviewableCanvas so they agree.
 * Returns true if the file's color is BRIGHT enough that we should
 * use the light-bg header treatment (dark icons, no bar). */
function shouldFloatHeader(typeInfo) {
  if (!FALLBACK_CONFIG.enabled) return false
  const lifted = liftColor(hexToRgb(typeInfo.bg), FALLBACK_CONFIG.minBrightness)
  return luminance(lifted) > FALLBACK_CONFIG.darkTextThreshold
}

const grainSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.7 0"/></filter><rect width="100%" height="100%" filter="url(#n)" opacity="1"/></svg>`
const grainDataUri = `url("data:image/svg+xml;utf8,${encodeURIComponent(grainSvg)}")`

/* ───────────────────────────────────────────────────────────────
 *  PDF cache + helpers (unchanged)
 * ─────────────────────────────────────────────────────────────── */
const pageCache = new Map()
const pdfDocCache = new Map()

function getOrCreatePdfEntry(url) {
  if (!pageCache.has(url)) {
    pageCache.set(url, { pages: new Map(), viewports: new Map(), numPages: 0 })
  }
  return pageCache.get(url)
}

function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve()
  if (window.__pdfJsLoadPromise) return window.__pdfJsLoadPromise
  window.__pdfJsLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
      resolve()
    }
    s.onerror = reject
    document.head.appendChild(s)
  })
  return window.__pdfJsLoadPromise
}

function getPdfDoc(url) {
  if (pdfDocCache.has(url)) return pdfDocCache.get(url)
  const promise = loadPdfJs().then(() => window.pdfjsLib.getDocument(url).promise)
  pdfDocCache.set(url, promise)
  return promise
}

function PdfPage({ url, pageNum, isFirst, totalPages, getPage, cachedSrc, cachedViewport }) {
  const [src, setSrc] = useState(cachedSrc || null)
  const [viewport, setViewport] = useState(cachedViewport || null)
  const containerRef = useRef(null)
  const startedRef = useRef(false)

  const startRender = useCallback(async () => {
    if (startedRef.current) return
    startedRef.current = true
    try {
      const result = await getPage(pageNum)
      if (result?.dataUrl) setSrc(result.dataUrl)
      if (result?.viewport) setViewport(result.viewport)
    } catch (err) {
      console.error('Page render failed', pageNum, err)
    }
  }, [getPage, pageNum])

  useEffect(() => {
    if (isFirst && !src) startRender()
  }, [isFirst, src, startRender])

  useEffect(() => {
    if (isFirst || src) return
    const el = containerRef.current
    if (!el) return
    const marginPx = Math.round(window.innerHeight * PDF_CONFIG.lazyLoadMargin)
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          startRender()
          observer.disconnect()
        }
      },
      { rootMargin: `${marginPx}px 0px ${marginPx}px 0px` }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isFirst, src, startRender])

  let placeholderHeight = '60vh'
  if (viewport) {
    const aspect = viewport.h / viewport.w
    placeholderHeight = `calc(min(900px, 100vw) * ${aspect})`
  }

  const borderRadius = pageNum === 1
    ? '12px 12px 0 0'
    : pageNum === totalPages
      ? '0 0 12px 12px'
      : '0'

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        background: src ? 'transparent' : '#1a1a1a',
        borderRadius,
        overflow: 'hidden',
        minHeight: src ? 0 : placeholderHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {src ? (
        <img src={src} alt={`Page ${pageNum}`} style={{ width: '100%', display: 'block', borderRadius }} />
      ) : (
        <div style={{ width: '24px', height: '24px', border: '2px solid rgba(255,255,255,0.15)', borderTopColor: 'rgba(255,255,255,0.5)', borderRadius: '50%', animation: 'docviewer-spin 0.8s linear infinite' }} />
      )}
      <div style={{ position: 'absolute', top: '8px', left: '10px', background: 'rgba(195, 195, 195, 0.6)', borderRadius: '20px', padding: '10px 20px', fontSize: '15px', color: '#000', fontWeight: '500' }}>
        {pageNum} of {totalPages}
      </div>
    </div>
  )
}

function UnpreviewableCanvas({ name, typeInfo, url }) {
  const rawRgb = hexToRgb(typeInfo.bg)
  const liftedRgb = liftColor(rawRgb, FALLBACK_CONFIG.minBrightness)
  const baseHsl = rgbToHsl(liftedRgb)
  const isLight = luminance(liftedRgb) > FALLBACK_CONFIG.darkTextThreshold

  const blob1Hsl = shiftHue(baseHsl, FALLBACK_CONFIG.blob1.hueShift)
  const blob2Hsl = shiftHue(baseHsl, FALLBACK_CONFIG.blob2.hueShift)
  const blob3Hsl = shiftHue(baseHsl, FALLBACK_CONFIG.blob3.hueShift)

  const heroColor = isLight ? 'rgba(20, 20, 22, 0.95)' : 'rgba(255, 255, 255, 0.96)'
  const labelColor = isLight
    ? `rgba(20, 20, 22, ${FALLBACK_CONFIG.labelOpacity})`
    : `rgba(255, 255, 255, ${FALLBACK_CONFIG.labelOpacity})`

  const pillTextHsl = { h: baseHsl.h, s: Math.min(100, baseHsl.s + 10), l: Math.min(45, baseHsl.l * 0.55) }
  const pillTextColor = hslStr(pillTextHsl, 1)

  const blob1 = FALLBACK_CONFIG.blob1
  const blob2 = FALLBACK_CONFIG.blob2
  const blob3 = FALLBACK_CONFIG.blob3

  const blobsBackground = [
    `radial-gradient(circle at ${blob3.x}% ${blob3.y}%, ${hslStr(blob3Hsl, blob3.opacity)} 0%, transparent ${blob3.size}%)`,
    `radial-gradient(circle at ${blob2.x}% ${blob2.y}%, ${hslStr(blob2Hsl, blob2.opacity)} 0%, transparent ${blob2.size}%)`,
    `radial-gradient(circle at ${blob1.x}% ${blob1.y}%, ${hslStr(blob1Hsl, blob1.opacity)} 0%, transparent ${blob1.size}%)`,
  ].join(', ')

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      background: '#050505',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: hslStr(baseHsl, FALLBACK_CONFIG.baseWashOpacity),
        pointerEvents: 'none',
      }} />
      <div
        className={FALLBACK_CONFIG.animate ? 'docviewer-blobs-drift' : ''}
        style={{
          position: 'absolute',
          inset: '-5%',
          width: '110%',
          height: '110%',
          background: blobsBackground,
          filter: 'blur(40px) saturate(140%)',
          WebkitFilter: 'blur(40px) saturate(140%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: grainDataUri,
        backgroundSize: '200px 200px',
        opacity: FALLBACK_CONFIG.grainOpacity,
        mixBlendMode: 'overlay',
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 24px',
      }}>
        <h1 style={{
          margin: 0,
          fontSize: FALLBACK_CONFIG.filenameFontSize,
          fontWeight: FALLBACK_CONFIG.filenameFontWeight,
          letterSpacing: FALLBACK_CONFIG.filenameLetterSpacing,
          color: heroColor,
          textAlign: 'center',
          wordBreak: 'break-word',
          lineHeight: 1.15,
          marginBottom: FALLBACK_CONFIG.filenameMarginBottom,
          fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          maxWidth: '92vw',
        }}>
          {truncateFilename(name, FALLBACK_CONFIG.filenameMaxLength)}
        </h1>
        <p style={{
          margin: 0,
          fontSize: FALLBACK_CONFIG.labelFontSize,
          fontWeight: FALLBACK_CONFIG.labelFontWeight,
          letterSpacing: FALLBACK_CONFIG.labelLetterSpacing,
          color: labelColor,
          textTransform: 'uppercase',
        }}>
          {typeInfo.label}
        </p>
      </div>

      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: 'absolute',
          bottom: FALLBACK_CONFIG.pillBottomOffset,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: `${FALLBACK_CONFIG.pillPaddingY} ${FALLBACK_CONFIG.pillPaddingX}`,
          background: FALLBACK_CONFIG.pillGlassBackground,
          backdropFilter: `saturate(${FALLBACK_CONFIG.pillGlassSaturate}) blur(${FALLBACK_CONFIG.pillGlassBlur})`,
          WebkitBackdropFilter: `saturate(${FALLBACK_CONFIG.pillGlassSaturate}) blur(${FALLBACK_CONFIG.pillGlassBlur})`,
          border: FALLBACK_CONFIG.pillGlassBorder,
          color: pillTextColor,
          borderRadius: FALLBACK_CONFIG.pillRadius,
          fontSize: FALLBACK_CONFIG.pillFontSize,
          fontWeight: FALLBACK_CONFIG.pillFontWeight,
          textDecoration: 'none',
          boxShadow: FALLBACK_CONFIG.pillShadow,
          whiteSpace: 'nowrap',
          zIndex: 2,
          fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        }}
      >
        Open in Browser
      </a>

      <style>{`
        @keyframes docviewer-blobs-drift {
          0%   { transform: translate(0, 0) rotate(0deg) scale(1); }
          33%  { transform: translate(2%, -1%) rotate(2deg) scale(1.04); }
          66%  { transform: translate(-1.5%, 1%) rotate(-1.5deg) scale(0.98); }
          100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        }
        .docviewer-blobs-drift {
          animation: docviewer-blobs-drift ${FALLBACK_CONFIG.animationDurationSec}s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .docviewer-blobs-drift { animation: none; }
        }
      `}</style>
    </div>
  )
}

function DocumentViewer({ url, filename, mimeType, onClose, onDelete, onShare }) {
  const name = filename
  const ext = name?.split('.').pop()?.toLowerCase()
  const isPdf = ext === 'pdf' || mimeType?.includes('pdf')
  const isImage = mimeType?.startsWith('image/')
  const isOfficeDoc = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)
  const officeViewerUrl = isOfficeDoc ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}` : null
  const typeInfo = getFileTypeInfo(name, mimeType)
  const showSaveButton = !isIOS()

  const [numPages, setNumPages] = useState(() => isPdf ? (pageCache.get(url)?.numPages || null) : null)
  const [pdfFailed, setPdfFailed] = useState(false)

  const renderQueueRef = useRef({ active: 0, waiting: [] })

  const getPage = useCallback(async (pageNum) => {
    const entry = getOrCreatePdfEntry(url)
    if (entry.pages.has(pageNum)) {
      return { dataUrl: entry.pages.get(pageNum), viewport: entry.viewports.get(pageNum) }
    }
    await new Promise((resolve) => {
      const q = renderQueueRef.current
      const tryStart = () => {
        if (q.active < PDF_CONFIG.maxConcurrentRenders) {
          q.active++
          resolve()
        } else {
          q.waiting.push(tryStart)
        }
      }
      tryStart()
    })
    try {
      if (entry.pages.has(pageNum)) {
        return { dataUrl: entry.pages.get(pageNum), viewport: entry.viewports.get(pageNum) }
      }
      const pdf = await getPdfDoc(url)
      const page = await pdf.getPage(pageNum)
      const scale = pageNum === 1 ? PDF_CONFIG.firstPageScale : PDF_CONFIG.renderScale
      const vp = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      canvas.width = vp.width
      canvas.height = vp.height
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
      const dataUrl = canvas.toDataURL('image/jpeg', PDF_CONFIG.jpegQuality)
      entry.pages.set(pageNum, dataUrl)
      entry.viewports.set(pageNum, { w: vp.width, h: vp.height })
      return { dataUrl, viewport: { w: vp.width, h: vp.height } }
    } finally {
      const q = renderQueueRef.current
      q.active--
      const next = q.waiting.shift()
      if (next) next()
    }
  }, [url])

  useEffect(() => {
    if (!isPdf || !url) return
    let cancelled = false
    const init = async () => {
      try {
        const entry = getOrCreatePdfEntry(url)
        if (entry.numPages) {
          setNumPages(entry.numPages)
          return
        }
        const pdf = await getPdfDoc(url)
        if (cancelled) return
        entry.numPages = pdf.numPages
        setNumPages(pdf.numPages)
        ;(async () => {
          for (let p = 1; p <= pdf.numPages; p++) {
            if (cancelled) return
            if (entry.viewports.has(p)) continue
            try {
              const page = await pdf.getPage(p)
              const vp = page.getViewport({ scale: PDF_CONFIG.renderScale })
              entry.viewports.set(p, { w: vp.width, h: vp.height })
            } catch {}
          }
        })()
      } catch (err) {
        console.error('PDF load failed:', err)
        if (!cancelled) setPdfFailed(true)
      }
    }
    init()
    return () => { cancelled = true }
  }, [isPdf, url])

  const renderFallback = () => FALLBACK_CONFIG.enabled
    ? <UnpreviewableCanvas name={name} typeInfo={typeInfo} url={url} />
    : (
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '16px', background: typeInfo.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span style={{ fontSize: '36px', fontWeight: '700', color: '#fff' }}>{typeInfo.letter}</span>
          </div>
          <p style={{ fontSize: '18px', color: '#fff', marginBottom: '8px' }}>{name}</p>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>This file type cannot be previewed.</p>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ padding: '12px 24px', background: 'var(--color-primary)', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '600', textDecoration: 'none' }}>Open in Browser</a>
        </div>
      </div>
    )

  /* ─── HEADER MODE DECISION ───
   *
   *  Are we showing the colored canvas right now? If yes, the header
   *  floats over it: no bar, no title, light-bg glass buttons. If no
   *  (image / real PDF / Office), the header keeps its original dark
   *  bar + title + white-glass buttons — those need the readable strip
   *  because they sit over varied content.
   *
   *  isShowingCanvas covers two cases:
   *    1) Non-previewable file (no PDF, no image, no Office)
   *    2) PDF that failed to load → falls back to canvas
   */
  const isShowingCanvas =
    FALLBACK_CONFIG.enabled &&
    (
      (!isImage && !isPdf && !isOfficeDoc) ||
      (isPdf && pdfFailed)
    )
  const lightHeader = isShowingCanvas && shouldFloatHeader(typeInfo)

  // Pick header chrome based on context
  const headerCircle = lightHeader ? glassCircleLight : glassCircle
  const headerPill = lightHeader ? glassPillLight : glassPill
  const dangerColor = lightHeader ? '#9b1c1c' : 'var(--color-danger)'
  const linkColor = lightHeader ? 'rgba(20, 20, 22, 0.9)' : 'var(--color-text-link)'

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--color-bg-app)', zIndex: 99999, display: 'flex', flexDirection: 'column' }}>
      {/* HEADER
       *  - When isShowingCanvas: floats over the canvas. No bar, no title.
       *  - Otherwise: same dark-bar header as before (image / PDF / Office).
       *
       *  We always make the header absolute when floating so the canvas
       *  underneath spans the full viewport.
       */}
      <div style={{
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: isShowingCanvas ? 'transparent' : 'rgba(0,0,0,0.45)',
        backdropFilter: isShowingCanvas ? 'none' : 'blur(12px)',
        WebkitBackdropFilter: isShowingCanvas ? 'none' : 'blur(12px)',
        position: isShowingCanvas ? 'absolute' : 'relative',
        top: 0, left: 0, right: 0,
        zIndex: 10,
      }}>
        <button onClick={onClose} style={{ ...headerCircle }}>
          <SFChevronLeft size={22} />
        </button>

        {/* Title slot: only show when NOT floating over the canvas.
         *  The canvas hero filename already serves as the title. */}
        {!isShowingCanvas ? (
          <p style={{ flex: 1, fontSize: '16px', fontWeight: '600', color: '#fff', margin: 0 }}>
            {truncateFilename(name, 34)}
          </p>
        ) : (
          <div style={{ flex: 1 }} />
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onShare} style={{ ...headerPill }}><SFShareUp size={24} /></button>
          {showSaveButton && (
            <button onClick={() => saveMediaWithPicker(url, name)} style={{ ...headerPill }}>
              <SFSaveDown size={24} />
            </button>
          )}
          <button onClick={onDelete} style={{ ...headerPill, color: dangerColor }}>
            <SFTrashFill size={24} color={dangerColor} />
          </button>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...headerPill, color: linkColor }}>
            <SFShareArrow size={20} />
          </a>
        </div>
      </div>

      {/* CONTENT */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        position: 'relative',
      }}>
        {isImage ? (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
        ) : isPdf ? (
          numPages ? (
            <div style={{ width: '100%', height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'var(--color-bg-app)', padding: '12px 8px' }}>
              <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {Array.from({ length: numPages }, (_, i) => {
                  const pageNum = i + 1
                  const entry = pageCache.get(url)
                  const cachedSrc = entry?.pages.get(pageNum)
                  const cachedViewport = entry?.viewports.get(pageNum)
                  return (
                    <PdfPage
                      key={pageNum}
                      url={url}
                      pageNum={pageNum}
                      isFirst={pageNum === 1}
                      totalPages={numPages}
                      getPage={getPage}
                      cachedSrc={cachedSrc}
                      cachedViewport={cachedViewport}
                    />
                  )
                })}
              </div>
              <style>{`@keyframes docviewer-spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          ) : pdfFailed ? (
            renderFallback()
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '16px', background: typeInfo.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '36px', fontWeight: '700', color: '#fff' }}>P</span>
              </div>
              <p style={{ fontSize: '16px', color: '#fff', margin: 0 }}>{name}</p>
              <div style={{ width: '24px', height: '24px', border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'docviewer-spin 0.8s linear infinite' }} />
              <style>{`@keyframes docviewer-spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )
        ) : isOfficeDoc ? (
          <div style={{ width: '100%', height: '100%', overflow: 'auto', display: 'flex', justifyContent: 'center', background: '#f5f5f7' }}>
            <iframe src={officeViewerUrl} style={{ width: '100%', maxWidth: '1100px', height: '100%', border: 'none', background: '#fff' }} title={name} />
          </div>
        ) : (
          renderFallback()
        )}
      </div>
    </div>
  )
}

export default DocumentViewer